import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  Snackbar,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  IconCash as FundIcon,
  IconCircleCheck as CheckCircleIcon
} from '@tabler/icons-react';
import { CenteredDollarLoader } from '../components/DollarSpinner';
import { SectionErrorBoundary } from '../components/ErrorBoundary';
import { CenteredLoader } from '../components/LoadingIndicator';
import api from '../services/api';
import { balancesFromTangible } from '../utils/tangibleBalances';
import { useGroup } from '../contexts/GroupContext.jsx';
import { PageHero, SectionContainer, Segmented, Switch, ActionBar, Sheet } from '../components/ds';
import { MaskedCurrency } from '../components/MaskedValue';
import { resolveAmountColor } from '../components/AmountDisplay';
import { formatCurrency } from '../utils/decimal';
import { ruleSummary, formatMoneyCompact, ordinalDay } from '../utils/paydayRuleSummary';
import { byName, groupBudgetsByCategory } from '../utils/budgetGroups';
import { useEngineStrings } from '../user_guides/i18n/useEngineStrings';

// ─── Payday calculation helpers (mirrors backend logic) ──────────────────────

function nextSemiMonthlyDate(afterDate, day1, day2) {
  const [d1, d2] = [day1, day2].sort((a, b) => a - b);
  const after = new Date(afterDate + 'T00:00:00Z');
  const y = after.getUTCFullYear();
  const m = after.getUTCMonth();
  const candidates = [
    new Date(Date.UTC(y, m, d1)),
    new Date(Date.UTC(y, m, d2)),
    new Date(Date.UTC(y, m + 1, d1)),
    new Date(Date.UTC(y, m + 1, d2)),
  ];
  for (const c of candidates) {
    if (c > after) return c.toISOString().slice(0, 10);
  }
  return new Date(Date.UTC(y, m + 2, d1)).toISOString().slice(0, 10);
}

function nextBiweeklyDate(afterDate, anchorDate) {
  const anchor = new Date(anchorDate + 'T00:00:00Z');
  const after = new Date(afterDate + 'T00:00:00Z');
  const msPerDay = 86400000;
  const diffDays = Math.floor((after - anchor) / msPerDay);
  const cyclesPassed = Math.floor(diffDays / 14);
  let next = new Date(anchor.getTime() + (cyclesPassed + 1) * 14 * msPerDay);
  if (next <= after) next = new Date(next.getTime() + 14 * msPerDay);
  return next.toISOString().slice(0, 10);
}

// #2099: mirrors backend dateMath.effectiveSchedule. A biweekly schedule funds
// on the 1st and 15th unless "Fund on paycheck days" is on.
function effectiveSchedule(config) {
  if (config.payFrequency === 'BIWEEKLY' && !config.fundOnPaycheckDays) {
    return { payFrequency: 'SEMI_MONTHLY', semiMonthlyDay1: 1, semiMonthlyDay2: 15 };
  }
  return config;
}

// #2107: counts from the same origin as GET /payday/status (lastFundedDate,
// else yesterday), so "Next" is the first payday Fund now will process.
function computePaydayPreview(rawConfig, lastFundedDate) {
  try {
    const config = effectiveSchedule(rawConfig);
    const from = lastFundedDate || new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    if (config.payFrequency === 'SEMI_MONTHLY') {
      const d1 = parseInt(config.semiMonthlyDay1);
      const d2 = parseInt(config.semiMonthlyDay2);
      if (!d1 || !d2 || d1 < 1 || d1 > 28 || d2 < 1 || d2 > 28 || d1 === d2) return null;
      return nextSemiMonthlyDate(from, d1, d2);
    }
    if (config.payFrequency === 'BIWEEKLY') {
      if (!config.biweeklyAnchorDate) return null;
      return nextBiweeklyDate(from, config.biweeklyAnchorDate);
    }
  } catch {
    return null;
  }
  return null;
}

function formatPaydayDate(isoStr) {
  if (!isoStr) return '';
  const [year, month, day] = isoStr.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function validateConfig(config) {
  if (!config.payFrequency) return 'Please select a pay frequency.';
  if (config.payFrequency === 'SEMI_MONTHLY') {
    const d1 = parseInt(config.semiMonthlyDay1);
    const d2 = parseInt(config.semiMonthlyDay2);
    if (!d1 || d1 < 1 || d1 > 28) return 'Day 1 must be between 1 and 28.';
    if (!d2 || d2 < 1 || d2 > 28) return 'Day 2 must be between 1 and 28.';
    if (d1 === d2) return 'Day 1 and Day 2 must be different.';
  }
  if (config.payFrequency === 'BIWEEKLY') {
    if (!config.biweeklyAnchorDate) return 'Please enter a biweekly anchor date.';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(config.biweeklyAnchorDate)) return 'Anchor date must be a valid date.';
    if (isNaN(new Date(config.biweeklyAnchorDate).getTime())) return 'Anchor date is invalid.';
  }
  return null;
}

// #2082: the UTC hour of 01:00 standard time in the browser's zone, whatever
// the season of the save, so Automatic funding runs at 01:05 local in winter
// and 02:05 in summer. Of January 1 and July 1 the larger offset is standard
// time (daylight time always shortens it). Rounded like the snapshot page.
function autoFundHourUtc() {
  const year = new Date().getFullYear();
  const standardOffsetMinutes = Math.max(
    new Date(year, 0, 1).getTimezoneOffset(),
    new Date(year, 6, 1).getTimezoneOffset()
  );
  return ((Math.round(1 + standardOffsetMinutes / 60) % 24) + 24) % 24;
}

function projectedMonthly(rule, payFrequency) {
  const isSplit = rule.fundingMode === 'SPLIT';
  const hasAmount = !!rule.amountA || (isSplit && !!rule.amountB);
  if (!rule.fundAmountEnabled || !hasAmount) return null;
  const a = parseFloat(rule.amountA) || 0;
  const b = parseFloat(rule.amountB) || 0;
  const c = parseFloat(rule.amountC) || 0;
  let total;
  if (payFrequency === 'SEMI_MONTHLY') {
    total = rule.fundingMode === 'UNIFORM' ? a * 2 : a + b;
  } else {
    total = rule.fundingMode === 'UNIFORM'
      ? (a * 26) / 12
      : (a * 13 + b * 13 + c * 2) / 12;
  }
  return total.toFixed(2);
}

const TRUE_UP_CADENCE_OPTIONS = [
  { value: 'EVERY_PAYDAY', label: 'Per paycheck' },
  { value: 'MONTHLY', label: '1st of the month' },
];
const FUNDING_MODE_OPTIONS = [
  { value: 'UNIFORM', label: 'Uniform' },
  { value: 'SPLIT', label: 'Split' },
];
// #2103: rides the true-up (same cadence, same balance read), so the switch is
// disabled while True-up is off, like the cadence control (#2104).
const RELEASE_LABEL = 'Release unspent balance, then fund';
const RELEASE_HELPER = 'A positive balance is reset to $0, then the payday funding amount is added';

const DEFAULT_RULE = {
  fundAmountEnabled: false,
  trueUpEnabled: false,
  fundingMode: 'UNIFORM',
  trueUpCadence: 'EVERY_PAYDAY',
  releaseUnspentEnabled: false,
  amountA: '',
  amountB: '',
  amountC: '',
};

const PaydayFunding = ({ isAuthReady = false }) => {
  const navigate = useNavigate();
  const { activeGroupId } = useGroup();
  const theme = useTheme();
  const { palette, ds } = theme;
  const strings = useEngineStrings();
  // v4 (#1377): xs edits rules in a bottom Sheet and funds via the pinned
  // ActionBar; sm+ edits in place on the recessed strip and funds from the
  // hero. JS-conditional so exactly one editor treatment exists in the DOM.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fundingPhase, setFundingPhase] = useState(null);
  const [fundingResult, setFundingResult] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [leaveDialog, setLeaveDialog] = useState(false);
  const [configDirty, setConfigDirty] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [openRuleId, setOpenRuleId] = useState(null);
  const [sheetRuleId, setSheetRuleId] = useState(null);

  const funding = fundingPhase === 'running';

  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState({
    payFrequency: 'SEMI_MONTHLY',
    semiMonthlyDay1: 1,
    semiMonthlyDay2: 15,
    biweeklyAnchorDate: '',
    fundOnPaycheckDays: false,
    autoFundEnabled: false,
    isActive: true,
    version: undefined,
  });
  const [originalFrequency, setOriginalFrequency] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [budgetCategories, setBudgetCategories] = useState([]);
  const [balances, setBalances] = useState({});
  const [ruleMap, setRuleMap] = useState({});

  useEffect(() => {
    if (isAuthReady && activeGroupId) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthReady, activeGroupId]);

  const loadData = async () => {
    if (!activeGroupId) return;
    setLoading(true);
    setError(null);
    try {
      const [configRes, statusRes, rulesRes, budgetsRes] = await Promise.all([
        api.get(activeGroupId, '/payday/config'),
        api.get(activeGroupId, '/payday/status'),
        api.get(activeGroupId, '/funding-rules'),
        api.get(activeGroupId, '/budgets'),
      ]);

      setStatus(statusRes.status || null);

      const loaded = configRes.config;
      const cfg = loaded
        ? {
            payFrequency: loaded.payFrequency || 'SEMI_MONTHLY',
            semiMonthlyDay1: loaded.semiMonthlyDay1 ?? 1,
            semiMonthlyDay2: loaded.semiMonthlyDay2 ?? 15,
            biweeklyAnchorDate: loaded.biweeklyAnchorDate || '',
            fundOnPaycheckDays: loaded.fundOnPaycheckDays === true,
            autoFundEnabled: loaded.autoFundEnabled === true,
            isActive: loaded.isActive !== false,
            version: loaded.version,
          }
        : {
            payFrequency: 'SEMI_MONTHLY',
            semiMonthlyDay1: 1,
            semiMonthlyDay2: 15,
            biweeklyAnchorDate: '',
            fundOnPaycheckDays: false,
            autoFundEnabled: false,
            isActive: true,
            version: undefined,
          };
      setConfig(cfg);
      setOriginalFrequency(cfg.payFrequency);

      const budgetList = budgetsRes.budgets || [];
      setBudgets(budgetList);
      // #2114: the category list rides on the same response.
      setBudgetCategories(budgetsRes.budgetCategories || []);

      const centsToDollarString = (cents) => {
        if (cents === null || cents === undefined) return '';
        const n = typeof cents === 'number' ? cents : Number(cents);
        if (!Number.isFinite(n)) return '';
        return (n / 100).toFixed(2);
      };
      const map = {};
      for (const rule of rulesRes.rules || []) {
        map[rule.budgetId] = {
          fundAmountEnabled: rule.fundAmountEnabled ?? false,
          trueUpEnabled: rule.trueUpEnabled ?? false,
          fundingMode: rule.fundingMode || 'UNIFORM',
          trueUpCadence: rule.trueUpCadence || 'EVERY_PAYDAY',
          releaseUnspentEnabled: rule.releaseUnspentEnabled === true,
          amountA: centsToDollarString(rule.amountCentsA),
          amountB: centsToDollarString(rule.amountCentsB),
          amountC: centsToDollarString(rule.amountCentsC),
        };
      }
      setRuleMap(map);

      if (budgetList.length > 0) {
        // #2158: one /tangible read instead of one /balance per budget; a
        // budget the response lacks reads null, as a failed call did before.
        api
          .get(activeGroupId, '/tangible')
          .then((t) => balancesFromTangible(t).budgets)
          .catch(() => ({}))
          .then((cents) => {
            const balMap = {};
            budgetList.forEach((b) => { balMap[b.budgetId] = b.budgetId in cents ? cents[b.budgetId] / 100 : null; });
            setBalances(balMap);
          });
      }
    } catch (err) {
      console.error('Error loading payday data:', err);
      setError(err.message || 'Failed to load payday data');
    } finally {
      setLoading(false);
      setIsDirty(false);
      setConfigDirty(false);
    }
  };

  const getRuleFor = (budgetId) => ruleMap[budgetId] || DEFAULT_RULE;

  const updateConfig = (updater) => {
    setIsDirty(true);
    setConfigDirty(true);
    setConfig(updater);
  };

  const updateRule = (budgetId, patch) => {
    setIsDirty(true);
    setRuleMap((prev) => ({
      ...prev,
      [budgetId]: { ...(prev[budgetId] || DEFAULT_RULE), ...patch },
    }));
  };

  const paydayPreview = useMemo(
    () => computePaydayPreview(config, status?.lastFundedDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.payFrequency, config.semiMonthlyDay1, config.semiMonthlyDay2, config.biweeklyAnchorDate, config.fundOnPaycheckDays, status?.lastFundedDate]
  );

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const handleBack = () => {
    if (isDirty) { setLeaveDialog(true); return; }
    navigate(-1);
  };

  const handleLeaveCancel = () => setLeaveDialog(false);

  const handleLeaveConfirm = () => {
    setLeaveDialog(false);
    navigate(-1);
  };

  // Saves the schedule AND every rule in one pass (the single PUT pair the
  // page has always issued). "Save schedule" and "Save rule" both land here;
  // returns true on success so editor callers know to close.
  const handleSave = async () => {
    const cfgError = validateConfig(config);
    if (cfgError) { showToast(cfgError, 'error'); return false; }

    const dollarStringToCents = (s) => {
      const trimmed = (s ?? '').toString().trim();
      if (trimmed === '') return 0;
      const n = parseFloat(trimmed);
      if (!Number.isFinite(n) || n < 0) return null;
      return Math.round(n * 100);
    };

    const rulesToSave = [];
    for (const budget of budgets) {
      const rule = getRuleFor(budget.budgetId);
      const isSplit = rule.fundingMode === 'SPLIT';
      const aCents = dollarStringToCents(rule.amountA);
      const bCents = dollarStringToCents(rule.amountB);
      const cCents = dollarStringToCents(rule.amountC);

      if (rule.fundAmountEnabled) {
        if (aCents === null) {
          showToast(`"${budget.name}": Amount A must be a non-negative number.`, 'error');
          return false;
        }
        if (isSplit) {
          if (bCents === null) {
            showToast(`"${budget.name}": Amount B must be a non-negative number.`, 'error');
            return false;
          }
          if (aCents === 0 && bCents === 0) {
            showToast(`"${budget.name}": at least one of Amount A or Amount B must be greater than $0.`, 'error');
            return false;
          }
        } else {
          if (aCents === 0) {
            showToast(`"${budget.name}" has "Fund Amount" on but no amount entered.`, 'error');
            return false;
          }
        }
      }
      if (!rule.fundAmountEnabled && !rule.trueUpEnabled) {
        continue;
      }
      rulesToSave.push({
        budgetId: String(budget.budgetId),
        fundAmountEnabled: rule.fundAmountEnabled,
        trueUpEnabled: rule.trueUpEnabled,
        fundingMode: rule.fundingMode,
        trueUpCadence: rule.trueUpCadence,
        releaseUnspentEnabled: rule.releaseUnspentEnabled,
        amountCentsA: aCents ?? 0,
        amountCentsB: isSplit ? (bCents ?? 0) : null,
        amountCentsC:
          config.payFrequency === 'BIWEEKLY' && isSplit && cCents !== null && cCents > 0
            ? cCents
            : null,
      });
    }

    setSaving(true);
    try {
      const configBody = { ...config, isActive: true };
      if (config.autoFundEnabled) configBody.autoFundHour = autoFundHourUtc();
      const [cfgRes] = await Promise.all([
        api.put(activeGroupId, '/payday/config', configBody),
        api.put(activeGroupId, '/funding-rules', { rules: rulesToSave, payFrequency: config.payFrequency }),
      ]);
      const newVersion = cfgRes?.config?.version;
      if (typeof newVersion === 'number') {
        setConfig(prev => ({ ...prev, version: newVersion }));
      }
      setOriginalFrequency(config.payFrequency);
      setIsDirty(false);
      setConfigDirty(false);
      const statusRes = await api.get(activeGroupId, '/payday/status');
      setStatus(statusRes.status || null);
      showToast('Budget automation settings saved');
      return true;
    } catch (err) {
      console.error('Error saving payday settings:', err);
      showToast(err.message || 'Failed to save settings', 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveAndCloseEditor = async () => {
    const ok = await handleSave();
    if (ok) {
      setOpenRuleId(null);
      setSheetRuleId(null);
    }
  };

  const handleFundNow = async () => {
    setFundingPhase('running');
    try {
      const res = await api.post(activeGroupId, '/payday/run');
      setFundingResult(res || null);
      setFundingPhase('complete');
      const statusRes = await api.get(activeGroupId, '/payday/status');
      setStatus(statusRes.status || null);
    } catch (err) {
      console.error('Error running funding:', err);
      setFundingPhase(null);
      if (err.status === 504 || err.status === 503) {
        // API Gateway timeout mid catch-up (#1341). Completed paydays are
        // already claimed and line writes are idempotent, so re-running
        // resumes where the run stopped.
        showToast('Funding run timed out mid-catch-up; progress is saved - run again to continue.', 'error');
      } else {
        showToast(err.message || 'Funding run failed', 'error');
      }
    }
  };

  if (!isAuthReady) {
    return <CenteredLoader message="Initializing..." />;
  }

  if (fundingPhase === 'running') {
    return (
      <Box sx={{ pb: 8 }}>
        <CenteredDollarLoader message="Processing budget automation..." minHeight="320px" />
      </Box>
    );
  }

  if (fundingPhase === 'complete') {
    return (
      <Box sx={{ pb: 8 }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={6}>
          <Box component="span" sx={{ color: 'success.main', display: 'inline-flex' }}>
            <CheckCircleIcon size={64} />
          </Box>
          <Typography variant="h5">Funding Complete</Typography>
          {fundingResult?.funded ? (
            <>
              <Typography variant="body1" color="text.secondary" textAlign="center">
                {fundingResult.paydaysProcessed?.length || 0} payday(s) processed, {fundingResult.linesWritten || 0} line(s) written: {formatCurrency((fundingResult.totalFundedCents || 0) / 100)} funded
              </Typography>
              {fundingResult.totalTrueUpCents > 0 && (
                <Typography variant="body2" color="error.main" textAlign="center">
                  True-ups applied: {formatCurrency(fundingResult.totalTrueUpCents / 100)}
                </Typography>
              )}
              {fundingResult.errors?.length > 0 && (
                <Typography variant="body2" color="warning.main" textAlign="center">
                  Some items could not be funded - run again to retry.
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body1" color="text.secondary">
              No funding was due at this time.
            </Typography>
          )}
          <Box display="flex" gap={2} mt={2}>
            <Button variant="outlined" onClick={() => navigate('/')}>
              Back to Dashboard
            </Button>
            <Button variant="contained" onClick={() => window.location.reload()}>
              Back to Funding
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  if (loading) {
    return <CenteredLoader message="Loading payday settings..." />;
  }

  const frequencyChanged = originalFrequency && config.payFrequency !== originalFrequency;
  const isBiweekly = config.payFrequency === 'BIWEEKLY';
  // The days funding actually lands on (#2099): biweekly funds on the 1st and
  // 15th unless the switch is on, so amounts and projections follow this, not
  // the pay frequency.
  const fundingSchedule = effectiveSchedule(config);
  const fundsOnPaychecks = fundingSchedule.payFrequency === 'BIWEEKLY';

  const hairline = `1px solid ${palette.divider}`;

  // v4 board day-box input: surface-2 fill on a hairline, 6px radius.
  const dayInputSx = {
    background: palette.surface.hover,
    border: hairline,
    borderRadius: '6px',
    padding: '6px 12px',
    fontSize: '13px',
    color: palette.text.primary,
    fontVariantNumeric: 'tabular-nums',
    width: '56px',
    textAlign: 'center',
    '&:focus-visible': { outline: `1px solid ${palette.interactive}`, outlineOffset: 0 },
  };

  const fundNowButton = (
    <Tooltip
      title={
        !status?.fundNowEnabled && status?.nextPaydayDate
          ? `Next payday: ${formatPaydayDate(status.nextPaydayDate)}`
          : ''
      }
    >
      <span style={isMobile ? { display: 'block' } : undefined}>
        <Button
          variant="contained"
          fullWidth={isMobile}
          startIcon={<FundIcon size={16} />}
          onClick={handleFundNow}
          disabled={!status?.fundNowEnabled || funding || saving}
        >
          Fund now
        </Button>
      </span>
    </Tooltip>
  );

  // Amount label and input are separate cells so the desktop grid (#2096) can
  // put labels on the funding-switch row and inputs on the mode row.
  const amountLabel = ({ label, sub, mobile }, keySuffix = '') => (
    <Box key={label + keySuffix} sx={{ fontSize: '12px', color: palette.text.muted, marginBottom: mobile ? '6px' : 0 }}>
      {label}
      {sub && (
        <Box component="span" sx={{ color: palette.text.dim }}>
          {' '}{sub}
        </Box>
      )}
    </Box>
  );

  const amountInput = ({ label, value, onChange, mobile }, keySuffix = '') => (
    <Box
      key={label + keySuffix}
      component="input"
      inputMode="decimal"
      aria-label={label}
      value={value}
      onChange={onChange}
      sx={{
        background: palette.surface.hover,
        border: hairline,
        borderRadius: mobile ? '8px' : '6px',
        padding: mobile ? '12px 14px' : '8px 12px',
        fontSize: mobile ? '15px' : '14px',
        width: mobile ? '100%' : '110px',
        boxSizing: 'border-box',
        color: palette.text.primary,
        fontVariantNumeric: 'tabular-nums',
        '&:focus-visible': { outline: `1px solid ${palette.interactive}`, outlineOffset: 0 },
      }}
    />
  );

  // Label over input: the phone sheet's stacked shape.
  const amountField = (spec) => (
    <Box key={spec.label} sx={{ flex: 1, minWidth: 0 }}>
      {amountLabel(spec)}
      {amountInput(spec)}
    </Box>
  );

  // Amount columns for the current mode/frequency; both editors render these specs.
  const amountSpecs = (budgetId, rule, mobile) => {
    const isSplit = rule.fundingMode === 'SPLIT';
    const specs = [{
      label: isSplit ? 'Amount A' : 'Amount',
      sub: isSplit && !fundsOnPaychecks ? `· ${ordinalDay(fundingSchedule.semiMonthlyDay1)}` : undefined,
      value: rule.amountA,
      onChange: (e) => updateRule(budgetId, { amountA: e.target.value }),
      mobile,
    }];
    if (isSplit) {
      specs.push({
        label: 'Amount B',
        sub: !fundsOnPaychecks ? `· ${ordinalDay(fundingSchedule.semiMonthlyDay2)}` : undefined,
        value: rule.amountB,
        onChange: (e) => updateRule(budgetId, { amountB: e.target.value }),
        mobile,
      });
    }
    if (isSplit && fundsOnPaychecks) {
      specs.push({
        label: 'Amount C',
        sub: '· 3rd paycheck months',
        value: rule.amountC,
        onChange: (e) => updateRule(budgetId, { amountC: e.target.value }),
        mobile,
      });
    }
    return specs;
  };

  // Desktop switch with its label beside it; `helper` adds a dim line under.
  const editorSwitch = ({ label, checked, onChange, disabled, helper }) => (
    <Box key={label} sx={{ fontSize: '13px', color: disabled ? palette.text.dim : palette.text.primary }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
        {label}
        <Switch checked={checked} onChange={onChange} disabled={disabled} ariaLabel={label} />
      </Box>
      {helper && <Box sx={{ fontSize: '12px', color: palette.text.dim, marginTop: '2px' }}>{helper}</Box>}
    </Box>
  );

  // Phone sheet switch row: label left, switch right.
  const editorSwitchRow = ({ label, checked, onChange, last, disabled, helper }) => (
    <Box
      key={label}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '13px 0',
        borderBottom: last ? 'none' : hairline,
      }}
    >
      <Box>
        <Box component="span" sx={{ fontSize: '14px', color: disabled ? palette.text.dim : palette.text.primary }}>{label}</Box>
        {helper && <Box sx={{ fontSize: '12px', color: palette.text.dim, marginTop: '2px' }}>{helper}</Box>}
      </Box>
      <Switch checked={checked} onChange={onChange} disabled={disabled} ariaLabel={label} />
    </Box>
  );

  // "Currently allocated: $X" for the editor header and the sheet title (#2096).
  const allocatedNode = (balance, style) => {
    if (typeof balance !== 'number') return null;
    return (
      <Box component="span" sx={{ fontSize: '12px', color: palette.text.dim, whiteSpace: 'nowrap' }}>
        Currently allocated: {balanceNode(balance, style)}
      </Box>
    );
  };

  const balanceNode = (balance, style) => {
    if (typeof balance !== 'number') return null;
    return (
      <MaskedCurrency
        value={balance}
        style={{
          fontWeight: 500,
          fontVariantNumeric: 'tabular-nums',
          color: resolveAmountColor(palette, 'row', balance < 0, balance === 0),
          ...style,
        }}
      />
    );
  };

  // sm+ in-place editor: recessed full-bleed strip inside the rules card
  // (margin cancels the SectionContainer 16px side padding).
  const renderRuleEditor = (budget, rule) => {
    const monthly = projectedMonthly(rule, fundingSchedule.payFrequency);
    const specs = rule.fundAmountEnabled ? amountSpecs(budget.budgetId, rule, false) : [];
    const fundingColumns = ['max-content', ...specs.map(() => '110px'), '1fr'].join(' ');
    return (
      <Box
        key={budget.budgetId}
        sx={{ margin: '0 -16px', backgroundColor: palette.surface.recessed, borderBottom: hairline }}
      >
        <Box
          role="button"
          tabIndex={0}
          aria-expanded
          onClick={() => setOpenRuleId(null)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenRuleId(null); }
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '8px 16px', cursor: 'pointer' }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ color: palette.text.primary, fontWeight: ds.font.weightMedium, fontSize: '14px' }}>
              {budget.name}
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right' }}>{allocatedNode(balances[budget.budgetId])}</Box>
        </Box>

        {/* Funding rows (#2096): row 1 switch + amount labels, row 2 mode + inputs + projection. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: fundingColumns,
            columnGap: '14px',
            rowGap: '6px',
            alignItems: 'center',
            padding: '2px 16px 12px',
          }}
        >
          {editorSwitch({
            label: 'Fund each payday',
            checked: rule.fundAmountEnabled,
            onChange: (v) => updateRule(budget.budgetId, { fundAmountEnabled: v }),
          })}
          {specs.map((spec) => amountLabel(spec, ':label'))}
          <Box />
          <Segmented
            inCard
            ariaLabel="Funding mode"
            options={FUNDING_MODE_OPTIONS}
            value={rule.fundingMode}
            onChange={(v) => updateRule(budget.budgetId, { fundingMode: v })}
            sx={{ justifySelf: 'start' }}
          />
          {specs.map((spec) => amountInput(spec, ':input'))}
          <Box sx={{ fontSize: '12px', color: palette.text.dim }}>
            {monthly !== null && `Projected: ${formatCurrency(Number(monthly))}/mo`}
          </Box>
        </Box>

        <Box sx={{ borderTop: hairline, margin: '0 16px' }} />

        {/* True-up rows: switch + cadence, then the release switch + Save. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            columnGap: '14px',
            rowGap: '10px',
            alignItems: 'center',
            padding: '12px 16px 14px',
          }}
        >
          {editorSwitch({
            label: 'True-up',
            checked: rule.trueUpEnabled,
            onChange: (v) => updateRule(budget.budgetId, { trueUpEnabled: v }),
          })}
          <Segmented
            inCard
            ariaLabel="True-up cadence"
            options={TRUE_UP_CADENCE_OPTIONS}
            value={rule.trueUpCadence}
            onChange={(v) => updateRule(budget.budgetId, { trueUpCadence: v })}
            disabled={!rule.trueUpEnabled}
            sx={{ justifySelf: 'start' }}
          />
          {editorSwitch({
            label: RELEASE_LABEL,
            helper: RELEASE_HELPER,
            checked: rule.releaseUnspentEnabled,
            onChange: (v) => updateRule(budget.budgetId, { releaseUnspentEnabled: v }),
            disabled: !rule.trueUpEnabled,
          })}
          <Box sx={{ justifySelf: 'end' }}>
            <Button size="small" variant="contained" onClick={saveAndCloseEditor} disabled={saving || funding}>
              Save rule
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderRuleRow = (budget, isLast) => {
    const rule = getRuleFor(budget.budgetId);
    if (!isMobile && openRuleId === budget.budgetId) return renderRuleEditor(budget, rule);

    const balance = balances[budget.budgetId];
    const off = !rule.fundAmountEnabled && !rule.trueUpEnabled;
    const monthly = projectedMonthly(rule, fundingSchedule.payFrequency);

    return (
      <Box
        key={budget.budgetId}
        role="button"
        tabIndex={0}
        aria-expanded={false}
        onClick={() => (isMobile ? setSheetRuleId(budget.budgetId) : setOpenRuleId(budget.budgetId))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (isMobile) setSheetRuleId(budget.budgetId); else setOpenRuleId(budget.budgetId);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '11px',
          padding: isMobile ? '10px 0' : '8px 0',
          borderBottom: isLast ? 'none' : hairline,
          opacity: off ? 0.55 : 1,
          cursor: 'pointer',
          transition: `opacity ${ds.motion.fast} ${ds.motion.easeOut}`,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ color: palette.text.primary, fontSize: '14px' }}>{budget.name}</Box>
          <Box sx={{ fontSize: '12px', color: palette.text.dim, marginTop: '1px' }}>{ruleSummary(rule)}</Box>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          {balanceNode(balance)}
          <Box sx={{ fontSize: '11px', color: palette.text.dim, marginTop: '1px' }}>
            {monthly !== null ? `${formatMoneyCompact(Number(monthly))}/mo` : 'Off'}
          </Box>
        </Box>
      </Box>
    );
  };

  const sheetBudget = budgets.find((b) => b.budgetId === sheetRuleId) || null;
  const sheetRule = sheetBudget ? getRuleFor(sheetBudget.budgetId) : null;
  const sheetMonthly = sheetRule ? projectedMonthly(sheetRule, fundingSchedule.payFrequency) : null;

  const scheduleNote = paydayPreview
    ? `Next: ${formatPaydayDate(paydayPreview)} · ${
        status?.lastFundedDate ? `last funded ${formatPaydayDate(status.lastFundedDate)}` : 'never funded'
      }`
    : null;

  const scheduleCard = (
    <SectionContainer title="Pay schedule" category="neutral">
      <Box sx={{ padding: '12px 0 14px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <Segmented
            ariaLabel="Pay frequency"
            options={[
              { value: 'SEMI_MONTHLY', label: 'Semi-monthly' },
              { value: 'BIWEEKLY', label: 'Biweekly' },
            ]}
            value={config.payFrequency}
            onChange={(v) => updateConfig((c) => ({
              ...c,
              payFrequency: v,
              // The switch only exists for a biweekly schedule (#2099).
              fundOnPaycheckDays: v === 'BIWEEKLY' ? c.fundOnPaycheckDays : false,
            }))}
          />
          {!isBiweekly ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: '6px', sm: '8px' },
                fontSize: '13px',
                marginTop: { xs: 0, sm: '12px' },
                width: { xs: 'auto', sm: '100%' },
              }}
            >
              <Box
                component="span"
                sx={{ color: palette.text.dim, fontSize: '12px', display: { xs: 'none', sm: 'inline' } }}
              >
                Paydays
              </Box>
              <Box
                component="input"
                type="number"
                min={1}
                max={28}
                step={1}
                aria-label="Payday 1"
                value={config.semiMonthlyDay1}
                onChange={(e) =>
                  updateConfig((c) => ({ ...c, semiMonthlyDay1: parseInt(e.target.value) || '' }))
                }
                sx={dayInputSx}
              />
              <Box component="span" sx={{ color: palette.text.dim }}>and</Box>
              <Box
                component="input"
                type="number"
                min={1}
                max={28}
                step={1}
                aria-label="Payday 2"
                value={config.semiMonthlyDay2}
                onChange={(e) =>
                  updateConfig((c) => ({ ...c, semiMonthlyDay2: parseInt(e.target.value) || '' }))
                }
                sx={dayInputSx}
              />
            </Box>
          ) : (
            <Box
              component="input"
              type="date"
              aria-label="Anchor date (a past payday)"
              value={config.biweeklyAnchorDate}
              onChange={(e) => updateConfig((c) => ({ ...c, biweeklyAnchorDate: e.target.value }))}
              sx={{ ...dayInputSx, width: '170px', textAlign: 'left', colorScheme: palette.mode }}
            />
          )}
        </Box>

        <Box sx={{ marginTop: '12px' }}>
          {editorSwitch({
            label: 'Fund on paycheck days',
            checked: config.fundOnPaycheckDays,
            onChange: (v) => updateConfig((c) => ({ ...c, fundOnPaycheckDays: v })),
            disabled: !isBiweekly,
            helper: 'Off: funds on the 1st and 15th. On: funds on each paycheck day from the anchor date.',
          })}
        </Box>

        <Box sx={{ marginTop: '12px' }}>
          {editorSwitch({
            label: 'Automatic funding',
            checked: config.autoFundEnabled,
            onChange: (v) => updateConfig((c) => ({ ...c, autoFundEnabled: v })),
            helper: 'Runs Fund now by itself early on each payday.',
          })}
        </Box>

        {scheduleNote && (
          <Box sx={{ fontSize: '12px', color: palette.text.dim, marginTop: '12px' }}>{scheduleNote}</Box>
        )}
        {isBiweekly && (
          <Box sx={{ fontSize: '12px', color: palette.text.dim, marginTop: '6px' }}>
            Anchor date is any past payday; paydays repeat every 14 days from it.
          </Box>
        )}

        {frequencyChanged && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            Pay frequency changed. Review your budget amounts below - A/B/C values may no
            longer apply correctly.
          </Alert>
        )}

        {configDirty && (
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={saving || funding}
            sx={{ marginTop: '14px' }}
          >
            {saving ? 'Saving...' : 'Save schedule'}
          </Button>
        )}
      </Box>
    </SectionContainer>
  );

  // #2114: one card per category, as on the Budgets page and Dashboard, then
  // Pending Income and Planned Expenses. With no categories the lone Default
  // card keeps the "Budget funding rules" title.
  const ruleGroups = [
    ...groupBudgetsByCategory(
      budgets.filter((b) => b.type === 0),
      budgetCategories,
      strings['budgetCategories.default']
    ).map((card) => ({
      key: card.key,
      title: budgetCategories.length === 0 ? 'Budget funding rules' : card.name,
      category: 'budgets',
      members: card.members,
    })),
    {
      key: 'pending-income',
      title: 'Pending Income',
      category: 'income',
      members: budgets.filter((b) => b.type === 3).sort(byName),
    },
    {
      key: 'planned-expenses',
      title: 'Planned Expenses',
      category: 'planned',
      members: budgets.filter((b) => b.type === 2).sort(byName),
    },
  ].filter((group) => group.members.length > 0);

  const rulesCard = ruleGroups.length === 0 ? (
    <SectionContainer title="Budget funding rules" category="budgets">
      <Alert severity="info" sx={{ mt: 1 }}>No budgets found.</Alert>
    </SectionContainer>
  ) : (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {ruleGroups.map((group, index) => (
        <SectionContainer
          key={group.key}
          data-funding-group={group.key}
          title={group.title}
          count={group.members.length}
          category={group.category}
          note={index === 0 && !isMobile ? 'click a rule to edit in place' : undefined}
        >
          {group.members.map((budget, i) => renderRuleRow(budget, i === group.members.length - 1))}
        </SectionContainer>
      ))}
    </Box>
  );

  return (
    <Box sx={{ pb: isMobile ? 16 : 10 }}>
      <PageHero
        label="Budget automation"
        back={{ label: 'Dashboard', onClick: handleBack }}
        aux={
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '20px' }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography component="div" sx={{ fontSize: '11px', color: 'text.dim' }}>
                Next payday
              </Typography>
              <Typography
                component="div"
                sx={{
                  fontSize: '14px',
                  fontVariantNumeric: 'tabular-nums',
                  color: status?.nextPaydayDate ? 'interactive' : 'text.dim',
                  marginTop: '2px',
                }}
              >
                {status?.nextPaydayDate ? formatPaydayDate(status.nextPaydayDate) : 'Not scheduled'}
              </Typography>
            </Box>
            {!isMobile && fundNowButton}
          </Box>
        }
      />

      <Box sx={{ mt: { xs: '12px', md: '14px' } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <SectionErrorBoundary title="Budget Automation Error">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '400px 1fr' },
              gap: 2,
              alignItems: 'start',
            }}
          >
            {scheduleCard}
            {rulesCard}
          </Box>

          {!isMobile && (
            <Box sx={{ textAlign: 'center', fontSize: '11px', color: palette.text.faint, mt: 3 }}>
              inactive rules are dimmed · rules save individually, Fund now runs the whole payday
            </Box>
          )}
        </SectionErrorBoundary>
      </Box>

      {isMobile && (
        <ActionBar hint="tap a rule to edit its funding">
          {fundNowButton}
        </ActionBar>
      )}

      {isMobile && (
        <Sheet
          open={sheetBudget != null}
          onClose={() => setSheetRuleId(null)}
          title={
            sheetBudget && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>{sheetBudget.name}</span>
                {allocatedNode(balances[sheetBudget.budgetId], { fontSize: '14px' })}
              </Box>
            )
          }
        >
          {sheetRule && (
            <>
              {editorSwitchRow({
                label: 'Fund each payday',
                checked: sheetRule.fundAmountEnabled,
                onChange: (v) => updateRule(sheetBudget.budgetId, { fundAmountEnabled: v }),
                last: true,
              })}
              <Segmented
                inCard
                ariaLabel="Funding mode"
                options={FUNDING_MODE_OPTIONS}
                value={sheetRule.fundingMode}
                onChange={(v) => updateRule(sheetBudget.budgetId, { fundingMode: v })}
              />
              {sheetRule.fundAmountEnabled && (
                <Box sx={{ display: 'flex', gap: '10px', marginTop: '12px', flexWrap: 'wrap' }}>
                  {amountSpecs(sheetBudget.budgetId, sheetRule, true).map((spec) => amountField(spec))}
                </Box>
              )}
              {sheetMonthly !== null && (
                <Box sx={{ fontSize: '12px', color: palette.text.dim, marginTop: '10px' }}>
                  Projected: {formatCurrency(Number(sheetMonthly))}/mo
                </Box>
              )}
              <Box sx={{ borderTop: hairline, marginTop: '14px' }} />
              {editorSwitchRow({
                label: 'True-up',
                checked: sheetRule.trueUpEnabled,
                onChange: (v) => updateRule(sheetBudget.budgetId, { trueUpEnabled: v }),
                last: true,
              })}
              <Segmented
                inCard
                ariaLabel="True-up cadence"
                options={TRUE_UP_CADENCE_OPTIONS}
                value={sheetRule.trueUpCadence}
                onChange={(v) => updateRule(sheetBudget.budgetId, { trueUpCadence: v })}
                disabled={!sheetRule.trueUpEnabled}
              />
              {editorSwitchRow({
                label: RELEASE_LABEL,
                helper: RELEASE_HELPER,
                checked: sheetRule.releaseUnspentEnabled,
                onChange: (v) => updateRule(sheetBudget.budgetId, { releaseUnspentEnabled: v }),
                disabled: !sheetRule.trueUpEnabled,
                last: true,
              })}
              <Button
                fullWidth
                variant="contained"
                onClick={saveAndCloseEditor}
                disabled={saving || funding}
                sx={{ marginTop: '12px', padding: '12px 0' }}
              >
                Save rule
              </Button>
            </>
          )}
        </Sheet>
      )}

      <Snackbar
        open={toast.open}
        autoHideDuration={toast.severity === 'error' ? null : 6000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Unsaved-changes leave guard (#1452) */}
      <Dialog open={leaveDialog} onClose={handleLeaveCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Leave without saving?</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Leave anyway?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLeaveCancel}>Stay</Button>
          <Button color="error" variant="contained" onClick={handleLeaveConfirm}>Leave</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaydayFunding;
